/**
 * Shared list/grid Links markup for website template pages + LiB.
 * Uses the same GridTemplate / LineTemplate as classic My Links so Home
 * (and other template pages) match Website Link-in-Bio Link Section CSS.
 */
import React from "react";
import "../StayUpToDate/LandingPageComponents/LinksComponent/Components/GridTemplate.css";
import "../StayUpToDate/LandingPageComponents/LinksComponent/Components/LineTemplate.css";
import "../StayUpToDate/LandingPageComponents/LinksComponent/LinksComponentDashboard.css";
import "../../../components/StayUpToDateComponent/UpToDateComponents/UpToDateLinks/UpToDateLinks.css";
import "./templateStayUpToDateEmbed.css";
import GridTemplate from "../StayUpToDate/LandingPageComponents/LinksComponent/Components/GridTemplate";
import LineTemplate from "../StayUpToDate/LandingPageComponents/LinksComponent/Components/LineTemplate";
import { withLayoutDisplayImage } from "../../../Utils/imageCrop/ensureLinkLayoutDefaultCrop";
import { TemplateFeedDesignBoundary } from "./TemplateFeedDesignBoundary";
import { templateDesignStaticProps } from "./resolveTheme";
import type { ThemeTokens } from "./types";

export type WebsiteTemplateMyLinksItem = {
  id: string;
  title: string;
  url: string;
  thumb?: string;
  imageCrop?: unknown;
};

/** Themed pulse skeleton — no white react-loading-skeleton (Loom link loader). */
function MyLinksThemedSkeleton({
  layout,
  isMobileView,
}: {
  layout: "list" | "grid";
  isMobileView?: boolean;
}) {
  const isGrid = layout === "grid";
  const count = isGrid ? (isMobileView ? 2 : 4) : 3;
  return (
    <div
      className="tpl-stayuptodate-embed mte-tpl-my-links-layout"
      role="status"
      aria-label="Loading"
      aria-busy="true"
    >
      <div
        className={`creator-card-grid-wrp mte-tpl-my-links-skel ${
          isGrid
            ? `grid-layout${isMobileView ? " mobile-view" : ""}`
            : "line-layout"
        }`}
      >
        {Array.from({ length: count }).map((_, i) =>
          isGrid ? (
            <div
              key={i}
              className="mte-tpl-my-links-skel-grid-card"
              style={{ animationDelay: `${i * 60}ms` }}
              aria-hidden
            >
              <div className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
            </div>
          ) : (
            <div
              key={i}
              className="mte-tpl-my-links-skel-line"
              style={{ animationDelay: `${i * 60}ms` }}
              aria-hidden
            >
              <div className="mte-tpl-skel-fill mte-tpl-skel-fill--cover" />
            </div>
          ),
        )}
      </div>
    </div>
  );
}

export function WebsiteTemplateMyLinksLayout({
  layout,
  items,
  showThumb = true,
  username = "",
  userId = "",
  componentName = "Links",
  isMobileView = false,
  isLoading = false,
  /** When true (editor canvas), links stay non-navigating. */
  disableNavigate = false,
  emptyHint,
  /** Website template theme — scopes DesignStaticProvider for My Links. */
  T,
}: {
  layout: "list" | "grid";
  items: WebsiteTemplateMyLinksItem[];
  showThumb?: boolean;
  username?: string;
  userId?: string;
  componentName?: string;
  isMobileView?: boolean;
  isLoading?: boolean;
  disableNavigate?: boolean;
  emptyHint?: React.ReactNode;
  T?: ThemeTokens | null;
}) {
  const isGrid = layout === "grid";
  const designTokens = T ? templateDesignStaticProps(T) : undefined;

  const inner = (() => {
    if (isLoading && items.length === 0) {
      return (
        <MyLinksThemedSkeleton layout={layout} isMobileView={isMobileView} />
      );
    }

    if (!items.length) {
      if (!emptyHint) return null;
      return (
        <div
          className="tpl-stayuptodate-embed mte-tpl-my-links-layout mte-lib-links-empty"
          data-lib-links-empty="true"
        >
          {emptyHint}
        </div>
      );
    }

    return (
      <div className="tpl-stayuptodate-embed mte-tpl-my-links-layout">
        <div
          className={`creator-card-grid-wrp ${
            isGrid
              ? `grid-layout${isMobileView ? " mobile-view" : ""}`
              : "line-layout"
          }`}
        >
          {items.map((item) => {
            const card = withLayoutDisplayImage(
              {
                id: item.id,
                link_name: item.title,
                link_url: disableNavigate ? "#" : item.url || "#",
                image_url: showThumb ? item.thumb || null : null,
                image_crop: item.imageCrop,
                index: 0,
              },
              isGrid ? "grid" : "line",
            );
            const shared = {
              card,
              onRemove: () => {},
              username,
              user_id: userId,
              componentName,
              isDashboard: false,
              isMobileView,
              from: "landing-page",
              pageFrom: "landing-page",
              designTokens,
            };
            return isGrid ? (
              <GridTemplate key={item.id} {...shared} />
            ) : (
              <LineTemplate key={item.id} {...shared} />
            );
          })}
        </div>
      </div>
    );
  })();

  if (!inner) return null;
  if (!T) return inner;
  return <TemplateFeedDesignBoundary T={T}>{inner}</TemplateFeedDesignBoundary>;
}
